import { Injectable } from '@angular/core'
import { PostConfigureProducerBody, ProducerDetail } from '@ftr/contracts/regional-api'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { plainToClass } from '@ftr/serialization'
import { switchMap } from 'rxjs'
import { RegionalApiClientSetupService } from '../api-client'

@Injectable({
  providedIn: 'root',
})
export class ProducersService extends RegionalApiClientSetupService {
  override regionalApiPath = 'producers'
  producerApiPath = 'producer'

  postConfiguration(courtSystemId: string, producerId: string, body: PostConfigureProducerBody): ApiResult<unknown> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.post<null>({
          headers: { courtSystemId },
          path: `/configure/${producerId}`,
          body,
        }),
      ),
    )
  }

  delete(courtSystemId: Uuid, producerId: Uuid): ApiResult {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.delete<null>({
          headers: { courtSystemId },
          path: `/${producerId}`,
        }),
      ),
    )
  }

  approveCourtSystemUpgrades(courtSystemId: Uuid, version?: string): ApiResult {
    return this.regionalApiClient(this.producerApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.post<null>({
          headers: { courtSystemId },
          path: `/court-system/${courtSystemId}/upgrade/approve`,
          body: { version },
        }),
      ),
    )
  }

  /**
   * The lastest version approved for an upgrade
   *
   * It may or may not have successfully been installed
   */
  getProducerVersionUpgrade(courtSystemId: Uuid, producerId: Uuid): ApiResult<{ version?: string } | null> {
    return this.regionalApiClient(this.producerApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.get<null>({
          headers: { courtSystemId },
          path: `/${producerId}/upgrade/approve`,
        }),
      ),
    )
  }

  approveUpgrade(courtSystemId: Uuid, producerId: Uuid, version?: string): ApiResult {
    return this.regionalApiClient(this.producerApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.post<null>({
          headers: { courtSystemId },
          path: `/${producerId}/upgrade/approve`,
          body: { version },
        }),
      ),
    )
  }

  listProducerDetail(courtSystemId: Uuid, locationId?: Uuid): ApiResult<ProducerDetail[]> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.get<unknown[]>(
          locationId
            ? {
                path: `/location/${locationId}`,
                headers: { courtSystemId },
              }
            : {
                headers: { courtSystemId },
              },
        ),
      ),
      mapData(list => list.map(x => plainToClass(ProducerDetail, x))),
    )
  }

  getProducerDetail(courtSystemId: Uuid, producerId: Uuid): ApiResult<ProducerDetail> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(client =>
        client.get<ProducerDetail>({
          headers: { courtSystemId },
          path: `/${producerId}`,
        }),
      ),
      mapData(x => plainToClass(ProducerDetail, x)),
    )
  }
}
